import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';

import { useLoggedInUserRoles } from '~/components/Auth';
import { useApplicationConfig } from '~/data/applicationConfig';
import { useCompanyData } from '~/data/company/useCompanyData';
import { CostCenter, useAllCostCenters } from '~/data/costCenters';
import { EmpBasicData, EmpPosition, EmploymentData } from '~/data/employee';
import { useAllEmployees } from '~/data/employees';
import { EmploymentDetailType } from '~/data/employment/useEmploymentDetails';
import { useAllEnums } from '~/data/enums';
import { useMyUnits, useOrgTree } from '~/data/orgunits';
import { useMySpecificUnits } from '~/data/orgunits/useMySpecificUnits';
import { useAllUnitPositions } from '~/data/unitPositions/useAllUnitPositions';
import { useEmployment } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/components/Employment/useEmployment';
import { useEmploymentData } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/components/Employment/useEmploymentData';
import { getManagerIds } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/components/ScheduleEmployment/data';
import { PersonnelGroups } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/components/ScheduleEmploymentDetail/useScheduleEmploymentDetail';
import { useContracts } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/useContracts';
import { Contracts } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/useScheduleChanges';
import { useSWR } from '~/hooks/useSWR';
import { isRoleHR, isRoleManager } from '~/services/Permissions';

interface EmploymentDataObject {
  empId: number;
  empBasicData: EmpBasicData;
  empPosition: EmpPosition;
  employmentDetails: EmploymentDetailType | undefined;
  employmentData: EmploymentData;
}
type PersonnelGroupFormkeys = {
  [key: `fEmplDetailPersonnelGroup${number}`]: string;
};

type CostCenterObj = {
  fEmpCostCenter: number | `${number}`;
  fEmpCostCenterIsDefault?: boolean;
};

export type NumberString = `${number}`;
export interface RehireForm {
  empPosition: {
    fEmpLegalEntity: number | `${number}`;
    fInternalPositionPosition: string;
    fEmpJobTitle: string;
    fEmpBusinessUnit1: number | `${number}`;
    fInternalPositionManager: number | `${number}`;
    fInternalPositionUnit: number | `${number}`;
    fEmpUnitPositions: `${number}`[];
    fInternalPositionIsCurrent: boolean;
    fInternalPositionInfo: string;
    fInternalPositionStartDate: string;
    fEmpCostCenter: number | `${number}` | undefined;
    fEmpCostCenters: CostCenterObj[];
    fEmpAddlCostCenters: CostCenter[] | string[];
    fInternalPositionIsPending: boolean;
  };
  employment: {
    fEmpContractNumber: string;
    fEmpMainContract: boolean;
    fEmpPersonNumber: string;
    fEmpBusinessUnit2: number | `${number}`;
    fEmpBusinessUnit3: number | `${number}`;
    fEmplType: string | undefined;
    fEmplCurrency: string;
    fEmplDateStart: string | null;
    fEmplDateArrival: string | null;
    fEmplTrialPeriod: string;
    fEmplTrialPeriodEndDate: string | null;
    fEmplPlannedEndDate: string | null;
    fEmplLastDateEmployment: string | null;
    fEmplTerminationDate: string | null;
    fEmplTerminationReason: string;
    fEmplFixedTermReason: string;
    fEmplInfo: string;
  };
  employmentDetails: {
    fEmplDetailStatus: string | undefined;
    fEmplDetailJobGrade: string | undefined;
    fEmplDetailTaxNumber: string;
    fEmplDetailVacBalCalcSixDays: number | `${number}`;
    fEmplDetailIsPending: boolean;
    fEmplDetailStartDate: string;
    fEmplDetailCalcHoursBalance: boolean;
  } & PersonnelGroups;
}

export type Form = {
  rehire: RehireForm;
};

export const useRehireEmpForm = (
  employmentDataObject: EmploymentDataObject,
) => {
  const { empId, empBasicData, empPosition, employmentDetails } =
    employmentDataObject;

  const { orgTree } = useOrgTree();
  const { companyData } = useCompanyData();
  const contracts = useContracts(Number(empId));
  const { allEmployees } = useAllEmployees();
  const { allEnums, isLoading: allEnumsLoading } = useAllEnums();
  const { myUnits } = useMyUnits(true);
  const { costCenters } = useAllCostCenters();
  const { unitPositions } = useAllUnitPositions();

  const { applicationConfig } = useApplicationConfig();
  const managerCanTerminate =
    !!applicationConfig?.fComConfig?.mgrConfig?.fComEnableMgrTerminateEmployee;
  const managerCanRehire =
    !!applicationConfig?.fComConfig?.mgrConfig?.fComEnableMgrRehireEmployee;

  const roles = useLoggedInUserRoles();
  const isHRUser = isRoleHR(roles);
  const isMGR = isRoleManager(roles);
  const { aMyHRUnits } = useMySpecificUnits();

  const restrictField = useMemo(() => {
    const inMyHRUnit = allEmployees[empId]
      ? aMyHRUnits.includes(allEmployees[empId].fEmpUnit)
      : false;

    return {
      manager: isMGR,
      hr: isHRUser,
      inMyHRUnit: inMyHRUnit,
      notAllowEmpTermDate:
        (!isHRUser && isMGR && !managerCanTerminate) ||
        (isHRUser && !inMyHRUnit) ||
        (!isHRUser && !isMGR),
      managerCantRehire:
        (!isHRUser && isMGR && !managerCanRehire) ||
        (isHRUser && !inMyHRUnit) ||
        (!isHRUser && !isMGR),
    };
  }, [
    empId,
    isMGR,
    isHRUser,
    aMyHRUnits,
    allEmployees,
    managerCanRehire,
    managerCanTerminate,
  ]);

  // Data: Managers
  const { data: managers } = useSWR<number[]>(
    'managers',
    () => getManagerIds(),
    {
      revalidateOnFocus: false,
    },
  );

  const {
    data: [employmentData],
    isLoading: employmentDataLoading,
  } = useEmploymentData({ employeeId: empId });

  const { terminationReasons, fixedTermReasons, trialPeriodOptions } =
    useEmployment(employmentData);

  const getCostCenterValues = useCallback(
    (positionUnit: number) => {
      const targetPU = positionUnit;

      const costCentersFiltered = costCenters.filter(
        (cc) =>
          cc.hasOwnProperty('fCostCenterUnits') &&
          cc.fCostCenterUnits.indexOf(targetPU) > -1,
      );
      return costCentersFiltered.length ? costCentersFiltered : costCenters;
    },
    [costCenters],
  );
  const costCenterValues = getCostCenterValues(
    empPosition.fInternalPositionUnit,
  );
  const costCenterValuesIndexed = costCenterValues.reduce(
    (acc, cc) => ({ ...acc, [cc.fCostCenterId]: cc }),
    {},
  ) as { [key: number]: CostCenter };
  const [employeeCostCenter, ...employeeAddlCostCenters] =
    empPosition.fEmpCostCenters;
  const fEmpAddlCostCenters = employeeAddlCostCenters.map(
    (cc) => costCenterValuesIndexed[cc.fEmpCostCenter],
  );

  const { fComMaxPersonnelGroups } = companyData.config;
  const personnelGroupIndexNumbers: number[] = Array.from(
    Array(fComMaxPersonnelGroups).keys(),
  );
  const pgGroupsDefaultValue = personnelGroupIndexNumbers.reduce(
    (acc, item) => {
      return {
        ...acc,
        [`fEmplDetailPersonnelGroup${item + 1}`]:
          employmentDetails?.[
            `fEmplDetailPersonnelGroup${item + 1}` as keyof EmploymentDetailType
          ] ?? '',
      };
    },
    {} as PersonnelGroupFormkeys,
  );

  const currencies = companyData.currencies.fComSupportedCurrencies;

  const contractsData = contracts.data as { contracts: Contracts[] };
  const isMainContract =
    contractsData.contracts.find((con: Contracts) => {
      return con.employeeId === empId;
    })?.isDefault ?? false;

  const initialFormValues = {
    rehire: {
      empPosition: {
        fEmpLegalEntity: `${empPosition.fEmpLegalEntity}`,
        fInternalPositionPosition: empPosition.fInternalPositionPosition,
        fEmpJobTitle: empPosition.fEmpJobTitle,
        fEmpBusinessUnit1: `${empPosition.fInternalPositionUnit}`,
        fInternalPositionManager: `${empPosition.fInternalPositionManager}`,
        fInternalPositionUnit: `${empPosition.fInternalPositionUnit}`,
        fEmpUnitPositions: empPosition.fEmpUnitPositions.map<`${number}`>(
          (up) => `${up.fEmpUnitPos}`,
        ),
        fInternalPositionIsCurrent: true,
        fInternalPositionInfo: empPosition.fInternalPositionInfo,
        fInternalPositionStartDate: empPosition.fInternalPositionStartDate,
        fEmpCostCenter: `${employeeCostCenter.fEmpCostCenter}`,
        fEmpCostCenters: [],
        fEmpAddlCostCenters,
        fInternalPositionIsPending: false,
      },
      employment: {
        fEmpContractNumber: '',
        fEmpMainContract: isMainContract,
        fEmpPersonNumber: empBasicData.fEmpPersonNumber,
        fEmpBusinessUnit2: `${-1}`,
        fEmpBusinessUnit3: `${-1}`,
        fEmplType: employmentData?.fEmplType,
        fEmplCurrency: employmentData?.fEmplCurrency,
        fEmplDateStart: '',
        fEmplDateArrival: '',
        fEmplTrialPeriod: employmentData?.fEmplTrialPeriod ?? '',
        fEmplTrialPeriodEndDate: '',
        fEmplPlannedEndDate: '',
        fEmplLastDateEmployment: '',
        fEmplTerminationDate: '',
        fEmplTerminationReason: '',
        fEmplFixedTermReason: '',
        fEmplInfo: employmentData?.fEmplInfo ?? '',
      },
      employmentDetails: {
        fEmplDetailStatus: employmentDetails?.fEmplDetailStatus,
        fEmplDetailJobGrade: employmentDetails?.fEmplDetailJobGrade ?? '',
        fEmplDetailTaxNumber: employmentDetails?.fEmplDetailTaxNumber ?? '',
        fEmplDetailVacBalCalcSixDays: `${
          employmentDetails?.fEmplDetailVacBalCalcSixDays ?? '5'
        }`,
        ...pgGroupsDefaultValue,
        fEmplDetailIsPending: false,
        fEmplDetailStartDate: employmentDetails?.fEmplDetailStartDate,
        fEmplDetailCalcHoursBalance:
          employmentDetails?.fEmplDetailCalcHoursBalance,
      },
    },
  } as unknown as Form;

  const {
    control,
    handleSubmit,
    getValues,
    watch,
    setValue,
    reset,
    formState,
  } = useForm<Form>({ defaultValues: initialFormValues });

  return {
    control,
    handleSubmit,
    getValues,
    setValue,
    watch,
    reset,
    formState,
    allEnumsLoading,
    employmentDataLoading,
    helperValues: {
      isMainContract,
      initialFormValues,
      allEnums,
      orgTree,
      myUnits,
      unitPositions,
      costCenterValues,
      managers,
      allEmployees,
      currencies,
      trialPeriodOptions,
      terminationReasons,
      fixedTermReasons,
      restrictField,
    },
  };
};
